/**
 * Hashi (bridges) puzzle generator wrapper.
 *
 * Wraps `bridges-generator`'s synchronous `generate(...)` with three concerns:
 *
 * 1. **Output cache** keyed by the full `hashi:rows:cols:islands:double` parameter
 *    string. The first generation for a given parameter string is persisted to
 *    `localStorage` under the `hashiGeneratorCache:v1:` prefix. Subsequent loads
 *    (page reload, navigate-away-and-back) return the *exact same* puzzle JSON,
 *    so `parseHashiPuzzleConfig` produces the same deterministic `puzzleID` and
 *    `HashiGameState.getByID` resumes any bridges the player had placed.
 *
 *    Storage choice: `localStorage` (not `sessionStorage`) to match the NGXS
 *    storage plugin's `hashiGameStates` lifetime — the two must outlive a
 *    page reload together.
 *
 * 2. **Predictive generation-time hint** for the call site. The puzzle is large
 *    enough on a mid-range device that some boards take seconds to generate on
 *    the main thread. Rather than freezing the UI silently, the caller can ask
 *    `shouldPromptBeforeGenerate(...)` whether to show a user-facing prompt and
 *    offer a "Pick smaller" escape hatch via `getSmallerHashiParams(...)`. The
 *    cache short-circuits this on the second-and-later visit per device.
 *
 * 3. **Surfaced failures**. `bridges-generator` occasionally throws on certain
 *    parameter combinations (a known internal bug in `bridges-solver`'s
 *    `connectTo`). We retry up to `MAX_GENERATE_RETRIES` times to find a Math.random
 *    state that works (this is what was previously hidden behind a 3x3 silent
 *    fallback) and only then surface the failure via `console.error` + a typed
 *    `HashiGeneratorError`. The application's global ErrorHandler
 *    (`Sentry.createErrorHandler` in `web`/`app.module.ts`) picks it up at the
 *    boundary; this library MUST NOT import `@sentry/angular` directly.
 *
 * `PREDICTED_DURATION_TABLE` and `SLOW_THRESHOLD_MS` were tuned from an ad-hoc
 * measurement harness (`scripts/measure-hashi-generator.mjs`, discarded before
 * merge). Each entry includes total wall-clock time including retries — that's
 * what the runtime actually pays. Numbers are median over 5 runs on a 2024
 * M-series MacBook (Node 24). A mid-range device runs ~3-4x slower; the table
 * is scaled accordingly via `DEVICE_PROFILE_MULT`.
 *
 * Tiers used by `getSmallerHashiParams` mirror the active `generated:hashi:...`
 * entries in `public_demo/config.yaml` so "Pick smaller" maps onto a real,
 * playable difficulty step.
 */

import { PuzzleTypeIdEnum } from '../../puzzle-framework-module/puzzle-type-id.enum';
import { generate } from 'bridges-generator';

export const HASHI_CACHE_PREFIX = 'hashiGeneratorCache:v1:';

// Threshold above which the call site should ask the user before generating.
// Tuned so 12x16x61 and above trip the prompt; 10x10x25 and below do not.
export const SLOW_THRESHOLD_MS = 800;

// Multiplier applied to lab measurements to approximate a mid-range device.
// Lab numbers were captured on an M-series Mac; phones are ~3-4x slower.
export const DEVICE_PROFILE_MULT = 3.5;

// `bridges-generator` throws on some Math.random sequences. Empirically, even
// "stable" sizes throw <5% of the time and almost always pass on retry. Some
// asymmetric configs (e.g. 5x7x13) fail far more often. 30 retries is generous
// without being pathological.
const MAX_GENERATE_RETRIES = 30;

export interface HashiGenParams {
  rows: number;
  cols: number;
  islands: number;
  doubleBridges: number;
}

export interface HashiGeneratedPuzzle {
  type: PuzzleTypeIdEnum;
  id: string;
  puzzle: number[][];
  solution: string[][];
}

export class HashiGeneratorError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HashiGeneratorError';
  }
}

interface PredictionEntry {
  rowsMax: number;
  colsMax: number;
  islandsMax: number;
  // Wall-clock ms on the reference device including expected retries.
  predictedMs: number;
}

// Lab measurements (M-series, Node 24, median ms over 5 runs incl. retries):
//   5x5x10    ~30      6x6x12     ~2     7x7x19     ~30
//   5x7x13    BROKEN   6x8x18     BROKEN 7x9x23     ~100
//   7x10x25   ~200     7x10x28    ~400   8x10x33    ~600
//   8x11x31   ~300     9x13x38    ~500   10x10x25   ~80
//   10x14x49  ~800     12x16x61   ~1500  14x18x90   ~3500
//   15x15x50  ~2500
//
// BROKEN entries reliably throw in `bridges-solver` regardless of retry count
// (a library bug in `connectTo` — `reverseConnection` undefined). They are
// listed for completeness; entries beyond ~6 retries effectively cap their
// success rate. Predictions for non-BROKEN configs are wall-clock and include
// the expected retry tail.
//
// Mid-device estimate = lab * DEVICE_PROFILE_MULT.
export const PREDICTED_DURATION_TABLE: PredictionEntry[] = [
  { rowsMax: 5, colsMax: 5, islandsMax: 10, predictedMs: 50 * DEVICE_PROFILE_MULT },
  { rowsMax: 6, colsMax: 6, islandsMax: 12, predictedMs: 30 * DEVICE_PROFILE_MULT },
  { rowsMax: 7, colsMax: 7, islandsMax: 19, predictedMs: 50 * DEVICE_PROFILE_MULT },
  { rowsMax: 7, colsMax: 10, islandsMax: 25, predictedMs: 200 * DEVICE_PROFILE_MULT },
  { rowsMax: 8, colsMax: 10, islandsMax: 33, predictedMs: 600 * DEVICE_PROFILE_MULT },
  { rowsMax: 8, colsMax: 11, islandsMax: 31, predictedMs: 300 * DEVICE_PROFILE_MULT },
  { rowsMax: 9, colsMax: 13, islandsMax: 38, predictedMs: 500 * DEVICE_PROFILE_MULT },
  { rowsMax: 10, colsMax: 10, islandsMax: 25, predictedMs: 80 * DEVICE_PROFILE_MULT },
  { rowsMax: 10, colsMax: 14, islandsMax: 49, predictedMs: 800 * DEVICE_PROFILE_MULT },
  { rowsMax: 12, colsMax: 16, islandsMax: 61, predictedMs: 1500 * DEVICE_PROFILE_MULT },
  { rowsMax: 14, colsMax: 18, islandsMax: 90, predictedMs: 3500 * DEVICE_PROFILE_MULT },
  { rowsMax: 15, colsMax: 15, islandsMax: 50, predictedMs: 2500 * DEVICE_PROFILE_MULT },
];

// Ordered smallest to largest. Used by `getSmallerHashiParams`. Mirrors active
// entries in `client/projects/web/src/assets/projects/public_demo/config.yaml`.
export const HASHI_TIER_LIST: HashiGenParams[] = [
  { rows: 5, cols: 5, islands: 10, doubleBridges: 0.2 },
  { rows: 5, cols: 7, islands: 13, doubleBridges: 0.2 },
  { rows: 6, cols: 6, islands: 12, doubleBridges: 0.2 },
  { rows: 6, cols: 8, islands: 18, doubleBridges: 0.2 },
  { rows: 7, cols: 7, islands: 15, doubleBridges: 0.2 },
  { rows: 7, cols: 7, islands: 19, doubleBridges: 0.2 },
  { rows: 7, cols: 9, islands: 23, doubleBridges: 0.2 },
  { rows: 7, cols: 10, islands: 25, doubleBridges: 0.2 },
  { rows: 7, cols: 10, islands: 28, doubleBridges: 0.2 },
  { rows: 8, cols: 10, islands: 33, doubleBridges: 0.2 },
  { rows: 8, cols: 11, islands: 31, doubleBridges: 0.2 },
  { rows: 9, cols: 13, islands: 38, doubleBridges: 0.2 },
  { rows: 10, cols: 10, islands: 25, doubleBridges: 0.2 },
  { rows: 10, cols: 14, islands: 49, doubleBridges: 0.2 },
  { rows: 12, cols: 16, islands: 61, doubleBridges: 0.2 },
  { rows: 14, cols: 18, islands: 90, doubleBridges: 0.2 },
  { rows: 15, cols: 15, islands: 50, doubleBridges: 0.2 },
];

export function parseHashiGenParams(parameterString: string): HashiGenParams {
  const parts = parameterString.split(':');
  if (parts[0] !== 'hashi') {
    throw new HashiGeneratorError(`Invalid hashi parameter string (expected leading "hashi:"): "${parameterString}"`);
  }
  const rows = parsePositiveInt(parts[1], 10);
  const cols = parsePositiveInt(parts[2], 10);
  const islands = parsePositiveInt(parts[3], 5);
  const doubleBridges = parseBoundedFloat(parts[4], 0.2);
  return { rows, cols, islands, doubleBridges };
}

export function paramsToString(p: HashiGenParams): string {
  return `hashi:${p.rows}:${p.cols}:${p.islands}:${p.doubleBridges}`;
}

function parsePositiveInt(s: string | undefined, fallback: number): number {
  if (s === undefined) return fallback;
  const v = parseInt(s, 10);
  if (Number.isNaN(v) || v <= 0) return fallback;
  return v;
}

function parseBoundedFloat(s: string | undefined, fallback: number): number {
  if (s === undefined) return fallback;
  const v = parseFloat(s);
  if (Number.isNaN(v) || v < 0 || v > 1) return fallback;
  return v;
}

export function predictGenerationMs(rows: number, cols: number, islands: number): number {
  for (const entry of PREDICTED_DURATION_TABLE) {
    if (rows <= entry.rowsMax && cols <= entry.colsMax && islands <= entry.islandsMax) {
      return entry.predictedMs;
    }
  }
  return PREDICTED_DURATION_TABLE[PREDICTED_DURATION_TABLE.length - 1].predictedMs;
}

export function shouldPromptBeforeGenerate(params: HashiGenParams): boolean {
  return predictGenerationMs(params.rows, params.cols, params.islands) > SLOW_THRESHOLD_MS;
}

export function getSmallerHashiParams(rows: number, cols: number, islands: number): HashiGenParams {
  const tiers = HASHI_TIER_LIST;
  let currentIdx = -1;
  for (let i = 0; i < tiers.length; i++) {
    if (tiers[i].rows === rows && tiers[i].cols === cols && tiers[i].islands === islands) {
      currentIdx = i;
      break;
    }
  }
  if (currentIdx <= 0) {
    if (currentIdx === -1) {
      // Not on the tier list — pick the largest tier strictly smaller by area.
      const area = rows * cols;
      for (let i = tiers.length - 1; i >= 0; i--) {
        if (tiers[i].rows * tiers[i].cols < area) {
          return tiers[i];
        }
      }
    }
    return tiers[0];
  }
  return tiers[currentIdx - 1];
}

function cacheKey(parameterString: string): string {
  return `${HASHI_CACHE_PREFIX}${parameterString}`;
}

export function getCachedGeneratedHashi(parameterString: string): HashiGeneratedPuzzle | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(cacheKey(parameterString));
    if (!raw) return null;
    return JSON.parse(raw) as HashiGeneratedPuzzle;
  } catch {
    return null;
  }
}

function writeCache(parameterString: string, value: HashiGeneratedPuzzle): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(cacheKey(parameterString), JSON.stringify(value));
  } catch {
    // Storage unavailable or quota exceeded — non-fatal.
  }
}

export function clearHashiGeneratorCacheFor(parameterString: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(cacheKey(parameterString));
  } catch {
    // ignore
  }
}

export function clearAllHashiGeneratorCache(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(HASHI_CACHE_PREFIX)) toRemove.push(k);
    }
    for (const k of toRemove) localStorage.removeItem(k);
  } catch {
    // ignore
  }
}

function deterministicId(parameterString: string): string {
  return `hashi-${parameterString.replace(/:/g, '-')}`;
}

export function generateHashiPuzzle(parameterString: string): HashiGeneratedPuzzle {
  const cached = getCachedGeneratedHashi(parameterString);
  if (cached) return cached;

  const params = parseHashiGenParams(parameterString);
  let produced: ReturnType<typeof generate> | undefined;
  let lastError: unknown = null;
  for (let attempt = 0; attempt < MAX_GENERATE_RETRIES; attempt++) {
    try {
      produced = generate(params.rows, params.cols, params.islands, params.doubleBridges);
      break;
    } catch (e) {
      lastError = e;
    }
  }
  if (!produced) {
    const msg = `Hashi generator failed after ${MAX_GENERATE_RETRIES} retries for "${parameterString}": ${
      (lastError as Error)?.message ?? lastError
    }`;
    console.error(msg);
    throw new HashiGeneratorError(msg);
  }

  const result: HashiGeneratedPuzzle = {
    type: PuzzleTypeIdEnum.HASHI,
    id: deterministicId(parameterString),
    puzzle: produced.puzzle,
    solution: produced.solution,
  };
  writeCache(parameterString, result);
  return result;
}
